import React, { useState, useEffect } from "react";
import {
  Modal,
  Upload,
  Select,
  Input,
  Descriptions,
  Table,
  Button,
  Checkbox,
  Alert,
  message,
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import GETJwtToken from "../../../redux/authentication/authentication";
import {
  useAnalyzeImportMutation,
  useExecuteImportMutation,
} from "../../../redux/import/importAPI";
import {
  ImportAnalysis,
  AdapterConflict,
  ConflictResolution,
  ImportSummary,
  UUIDImportPolicy,
} from "../../../interfaces/Import.interface";
import useGetAllOrganizationsLight from "../../../customHooks/GET_requests/useGetAllOrganizationsLight";
import { formatUuidT } from "../../../flatbuffers/display";
import { IMPORT_FILE_ACCEPT, IMPORT_FILE_HINT } from "../../setupWizard/utils/importFileTypes";
import ImportWarnings from "../../setupWizard/components/ImportWarnings";

const { Dragger } = Upload;

const defaultUuidPolicy: UUIDImportPolicy = {
  preserve_organizations: false,
  preserve_servers: false,
  preserve_adapters: false,
  preserve_channels: false,
  preserve_banks: false,
  preserve_lifts: false,
  preserve_roles: false,
  preserve_users: false,
  preserve_keys: false,
  preserve_notifications: false,
};

const uuidPolicyLabels: { key: keyof UUIDImportPolicy; label: string }[] = [
  { key: "preserve_organizations", label: "Organizations" },
  { key: "preserve_servers", label: "Servers" },
  { key: "preserve_adapters", label: "Adapters" },
  { key: "preserve_channels", label: "Channels" },
  { key: "preserve_banks", label: "Banks" },
  { key: "preserve_lifts", label: "Lifts" },
  { key: "preserve_roles", label: "Roles" },
  { key: "preserve_users", label: "Users" },
  { key: "preserve_keys", label: "Keys" },
  { key: "preserve_notifications", label: "Notifications" },
];

interface OrganizationImportModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function OrganizationImportModal({ open, onClose, onSuccess }: OrganizationImportModalProps) {
  const [step, setStep] = useState<"upload" | "analysis" | "summary">("upload");
  const [file, setFile] = useState<File | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const { organizationsLight } = useGetAllOrganizationsLight();

  useEffect(() => {
    GETJwtToken().then(setToken);
  }, []);

  const [parentOrgId, setParentOrgId] = useState<string>("");
  const [database, setDatabase] = useState<string>("edge");
  const [analysis, setAnalysis] = useState<ImportAnalysis | null>(null);
  const [resolutions, setResolutions] = useState<Record<string, ConflictResolution>>({});
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [adoptExistingUsers, setAdoptExistingUsers] = useState(true);
  const [uuidPolicy, setUuidPolicy] = useState<UUIDImportPolicy>(defaultUuidPolicy);

  // Default to "Liftnet" root org when modal opens, fall back to first org
  React.useEffect(() => {
    if (open && !parentOrgId && organizationsLight.length > 0) {
      const root = organizationsLight.find((o) => String(o.name ?? "").toLowerCase() === "liftnet");
      setParentOrgId(root ? formatUuidT(root.id) : formatUuidT(organizationsLight[0].id));
    }
  }, [open, parentOrgId, organizationsLight]);

  const [analyzeImport, { isLoading: isAnalyzing }] = useAnalyzeImportMutation();
  const [executeImport, { isLoading: isExecuting }] = useExecuteImportMutation();

  const resetState = () => {
    setStep("upload");
    setFile(null);
    setParentOrgId("");
    setDatabase("edge");
    setAnalysis(null);
    setResolutions({});
    setSummary(null);
    setAdoptExistingUsers(true);
    setUuidPolicy(defaultUuidPolicy);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleAnalyze = async () => {
    if (!file || !parentOrgId) {
      message.error("Please select a file and parent organization");
      return;
    }

    try {
      const result: any = await analyzeImport({
        token,
        body: {
          file,
          parent_org_id: parentOrgId,
          database,
          uuid_policy: uuidPolicy,
        },
      }).unwrap();

      const analysisData: ImportAnalysis = result.data;
      setAnalysis(analysisData);

      const initialResolutions: Record<string, ConflictResolution> = {};
      if (analysisData.adapter_conflicts) {
        analysisData.adapter_conflicts.forEach((conflict: AdapterConflict) => {
          const key = `${conflict.imported_name}|${conflict.imported_version}`;
          initialResolutions[key] = {
            adapter_name: conflict.imported_name,
            adapter_version: conflict.imported_version,
            action: "skip",
          };
        });
      }
      setResolutions(initialResolutions);
      setStep("analysis");
    } catch (err: any) {
      message.error(err?.data?.message || "Analysis failed");
    }
  };

  const handleExecute = async () => {
    if (!analysis) return;

    try {
      const result: any = await executeImport({
        token,
        body: {
          analysis_id: analysis.id,
          resolutions: Object.values(resolutions),
          adopt_existing_users: adoptExistingUsers,
        },
      }).unwrap();

      setSummary(result.data);
      setStep("summary");
      onSuccess();
    } catch (err: any) {
      message.error(err?.data?.message || "Import failed");
    }
  };

  const updateResolution = (key: string, action: "skip" | "replace" | "rename") => {
    setResolutions((prev) => ({
      ...prev,
      [key]: { ...prev[key], action },
    }));
  };

  const toggleUuidPolicy = (policyKey: keyof UUIDImportPolicy, checked: boolean) => {
    setUuidPolicy((prev) => ({
      ...prev,
      [policyKey]: checked,
    }));
  };

  const conflictColumns = [
    {
      title: "Adapter",
      dataIndex: "imported_name",
      key: "imported_name",
    },
    {
      title: "Version",
      dataIndex: "imported_version",
      key: "imported_version",
    },
    {
      title: "Differences",
      key: "diffs",
      render: (_: any, record: AdapterConflict) =>
        record.field_diffs?.map((d) => `${d.field}: ${d.existing} → ${d.imported}`).join(", ") ||
        "None",
    },
    {
      title: "Action",
      key: "action",
      render: (_: any, record: AdapterConflict) => {
        const key = `${record.imported_name}|${record.imported_version}`;
        return (
          <Select
            value={resolutions[key]?.action || "skip"}
            onChange={(val: "skip" | "replace" | "rename") => updateResolution(key, val)}
            style={{ width: 120 }}
          >
            <Select.Option value="skip">Skip</Select.Option>
            <Select.Option value="replace">Replace</Select.Option>
            <Select.Option value="rename">Rename</Select.Option>
          </Select>
        );
      },
    },
  ];

  const renderUploadStep = () => (
    <>
      <div style={{ marginBottom: 16 }}>
        <span>Parent Organization</span>
        <Select
          placeholder="Select parent organization"
          value={parentOrgId || undefined}
          onChange={(val: string) => setParentOrgId(val)}
          style={{ width: "100%", marginTop: 4 }}
          showSearch
          optionFilterProp="children"
        >
          {organizationsLight.map((org) => (
            <Select.Option key={formatUuidT(org.id)} value={formatUuidT(org.id)}>
              {String(org.name ?? "")}
            </Select.Option>
          ))}
        </Select>
      </div>
      <div style={{ marginBottom: 16 }}>
        <span>Database Name</span>
        <Input
          value={database}
          onChange={(e) => setDatabase(e.target.value)}
          placeholder="edge"
          style={{ marginTop: 4 }}
        />
      </div>
      <div style={{ marginBottom: 16 }}>
        <span>UUID Preservation</span>
        <div style={{ marginTop: 8 }}>
          {uuidPolicyLabels.map((item) => (
            <div key={item.key} style={{ marginBottom: 6 }}>
              <Checkbox
                checked={uuidPolicy[item.key]}
                onChange={(e) => toggleUuidPolicy(item.key, e.target.checked)}
              >
                Keep source UUIDs for {item.label}
              </Checkbox>
            </div>
          ))}
        </div>
        <p style={{ fontSize: 12, opacity: 0.7, marginBottom: 0, marginTop: 6 }}>
          Unchecked items will receive new UUIDs while relationships are remapped automatically.
        </p>
      </div>
      <Dragger
        accept={IMPORT_FILE_ACCEPT}
        maxCount={1}
        beforeUpload={(f) => {
          setFile(f);
          return false;
        }}
        onRemove={() => setFile(null)}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">Click or drag export file to upload</p>
        <p className="ant-upload-hint">{IMPORT_FILE_HINT}</p>
      </Dragger>
    </>
  );

  const renderAnalysisStep = () => (
    <>
      {analysis && (
        <>
          {analysis.schema_version === "legacy_nmh" && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Legacy schema detected"
              description={
                <>
                  <div>
                    This dump is in the older <code>nmh</code> schema. The following org-scoped
                    columns were synthesized from the imported organization:
                  </div>
                  <ul style={{ marginTop: 8, marginBottom: 0 }}>
                    {(analysis.backfilled_columns ?? []).map((col) => (
                      <li key={col}>
                        <code>{col}</code>
                      </li>
                    ))}
                  </ul>
                </>
              }
            />
          )}
          {analysis.schema_version === "unknown" && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message="Ambiguous schema"
              description="The dump matches neither the current nor the legacy schema fully. Execute is disabled — please verify the source file and re-upload."
            />
          )}
          <Descriptions title="Import Counts" bordered size="small" column={2}>
            <Descriptions.Item label="Organizations">{analysis.org_count}</Descriptions.Item>
            <Descriptions.Item label="Servers">{analysis.server_count}</Descriptions.Item>
            <Descriptions.Item label="Adapters">{analysis.adapter_count}</Descriptions.Item>
            <Descriptions.Item label="Channels">{analysis.channel_count}</Descriptions.Item>
            <Descriptions.Item label="Banks">{analysis.bank_count}</Descriptions.Item>
            <Descriptions.Item label="Lifts">{analysis.lift_count}</Descriptions.Item>
            <Descriptions.Item label="Keys">{analysis.key_count}</Descriptions.Item>
            <Descriptions.Item label="Notifications">
              {analysis.notification_count}
            </Descriptions.Item>
            <Descriptions.Item label="Users">{analysis.user_count}</Descriptions.Item>
            <Descriptions.Item label="Roles">{analysis.role_count}</Descriptions.Item>
            <Descriptions.Item label="Leading Zeroes">
              {analysis.leading_zeroes_count}
            </Descriptions.Item>
            <Descriptions.Item label="Contact Infos">
              {analysis.contact_info_count}
            </Descriptions.Item>
            <Descriptions.Item label="Parameters">{analysis.parameter_count}</Descriptions.Item>
            <Descriptions.Item label="Action Banks">{analysis.action_bank_count}</Descriptions.Item>
          </Descriptions>
          {analysis.existing_user_emails && analysis.existing_user_emails.length > 0 && (
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 16 }}
              message={`${analysis.existing_user_emails.length} user(s) already exist in the system`}
              description={
                <div>
                  <p style={{ marginBottom: 8 }}>{analysis.existing_user_emails.join(", ")}</p>
                  <Checkbox
                    checked={adoptExistingUsers}
                    onChange={(e) => setAdoptExistingUsers(e.target.checked)}
                  >
                    Move existing users to this organization
                  </Checkbox>
                  <p
                    style={{
                      fontSize: 12,
                      opacity: 0.7,
                      marginTop: 4,
                      marginBottom: 0,
                    }}
                  >
                    When checked, existing users will be reassigned to the new organization with
                    matching roles. When unchecked, they will be linked by reference only.
                  </p>
                </div>
              }
            />
          )}
          {analysis.adapter_conflicts && analysis.adapter_conflicts.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <h4>Adapter Conflicts</h4>
              <Table
                dataSource={analysis.adapter_conflicts}
                columns={conflictColumns}
                rowKey={(record) => `${record.imported_name}|${record.imported_version}`}
                pagination={false}
                size="small"
              />
            </div>
          )}
        </>
      )}
    </>
  );

  const renderSummaryStep = () => (
    <>
      {summary && (
        <Descriptions title="Import Summary" bordered size="small" column={2}>
          <Descriptions.Item label="Organizations Created">
            {summary.orgs_created}
          </Descriptions.Item>
          <Descriptions.Item label="Servers Created">{summary.servers_created}</Descriptions.Item>
          <Descriptions.Item label="Adapters Created">{summary.adapters_created}</Descriptions.Item>
          <Descriptions.Item label="Adapters Skipped">{summary.adapters_skipped}</Descriptions.Item>
          <Descriptions.Item label="Adapters Replaced">
            {summary.adapters_replaced}
          </Descriptions.Item>
          <Descriptions.Item label="Channels Created">{summary.channels_created}</Descriptions.Item>
          <Descriptions.Item label="Banks Created">{summary.banks_created}</Descriptions.Item>
          <Descriptions.Item label="Lifts Created">{summary.lifts_created}</Descriptions.Item>
          <Descriptions.Item label="Keys Created">{summary.keys_created}</Descriptions.Item>
          <Descriptions.Item label="Notifications Created">
            {summary.notifications_created}
          </Descriptions.Item>
          <Descriptions.Item label="Users Created">{summary.users_created}</Descriptions.Item>
          {summary.users_adopted > 0 && (
            <Descriptions.Item label="Users Adopted">{summary.users_adopted}</Descriptions.Item>
          )}
          {summary.users_skipped > 0 && (
            <Descriptions.Item label="Users Skipped (existing)">
              {summary.users_skipped}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Roles Created">{summary.roles_created}</Descriptions.Item>
          {summary.roles_skipped > 0 && (
            <Descriptions.Item label="Roles Skipped (existing)">
              {summary.roles_skipped}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Leading Zeroes Created">
            {summary.leading_zeroes_created}
          </Descriptions.Item>
          <Descriptions.Item label="Contact Infos Created">
            {summary.contact_infos_created}
          </Descriptions.Item>
          <Descriptions.Item label="Parameters Created">
            {summary.parameters_created}
          </Descriptions.Item>
          <Descriptions.Item label="Action Banks Created">
            {summary.action_banks_created}
          </Descriptions.Item>
          <Descriptions.Item label="Groups Created">{summary.groups_created}</Descriptions.Item>
          <Descriptions.Item label="Holidays Created">{summary.holidays_created}</Descriptions.Item>
        </Descriptions>
      )}
      <ImportWarnings warnings={summary?.warnings} />
    </>
  );

  const getTitle = () => {
    switch (step) {
      case "upload":
        return "Import Organization - Upload";
      case "analysis":
        return "Import Organization - Analysis";
      case "summary":
        return "Import Organization - Summary";
      default:
        return "Import Organization";
    }
  };

  const getFooter = () => {
    switch (step) {
      case "upload":
        return [
          <Button key="cancel" onClick={handleClose}>
            Cancel
          </Button>,
          <Button
            key="analyze"
            type="primary"
            onClick={handleAnalyze}
            loading={isAnalyzing}
            disabled={!file || !parentOrgId}
          >
            Analyze
          </Button>,
        ];
      case "analysis":
        return [
          <Button key="back" onClick={() => setStep("upload")}>
            Back
          </Button>,
          <Button
            key="execute"
            type="primary"
            onClick={handleExecute}
            loading={isExecuting}
            disabled={analysis?.schema_version === "unknown"}
          >
            Execute Import
          </Button>,
        ];
      case "summary":
        return [
          <Button key="close" type="primary" onClick={handleClose}>
            Close
          </Button>,
        ];
      default:
        return [];
    }
  };

  return (
    <Modal
      title={getTitle()}
      open={open}
      onCancel={handleClose}
      footer={getFooter()}
      width={720}
      destroyOnClose
    >
      {step === "upload" && renderUploadStep()}
      {step === "analysis" && renderAnalysisStep()}
      {step === "summary" && renderSummaryStep()}
    </Modal>
  );
}

export default OrganizationImportModal;
